import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../../Config';

export default function RoomUnlockScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const { roomNumber, roomID, guestName, hasDigiLock, reservationId } = location.state || {};

  const [passKey, setPassKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUnlock = async () => {
    if (!reservationId || !roomID) {
      setError('Missing reservation ID or room ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = `${config.apiBaseUrl}/guest/get-pass-key/${reservationId}/${roomID}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed. Please contact the team for support');
      }

      const data = await response.json();

      if (!data.success || !data.data?.password) {
        throw new Error(data.message || 'Pass key not found in response');
      }

      const key = data.data.password;
      setPassKey(key);

      navigate('/unlock-success', { state: { passKey: key } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          py: 2.5,
          px: 2,
          mt: 1,
        }}
      >
        <IconButton
          sx={{
            position: 'absolute',
            left: 20,
            color: '#000',
            p: 0,
          }}
          aria-label="Go back"
          onClick={handleBack}
        >
          <ArrowBackIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#000',
            fontSize: '20px',
          }}
        >
          Unlock Your Room
        </Typography>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          flex: 1,
          px: 3,
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            borderRadius: '28px',
            mb: 8,
            width: '100%',
            backgroundColor: '#f0f0f0',
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              py: 2.5,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#000',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              {guestName}, Room no. {roomNumber}
            </Typography>

            {loading && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Typography
                sx={{
                  mt: 2,
                  color: 'red',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        </Card>

        {/* Lock Icon */}
        <Box
          sx={{
            mb: 8,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 40,
                border: '8px solid #999',
                borderBottom: 'none',
                borderRadius: '30px 30px 0 0',
                mb: -1,
              }}
            />
            <Box
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#2e7d7b',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 4,
                    height: 12,
                    backgroundColor: 'white',
                    borderRadius: '0 0 2px 2px',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {!hasDigiLock ? (
          <Typography
            sx={{
              textAlign: 'center',
              color: '#d32f2f',
              fontSize: '16px',
              fontWeight: 500,
              mb: 6,
            }}
          >
            Please collect your physical key from the reception.
          </Typography>
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={handleUnlock}
            disabled={loading}
            sx={{
              backgroundColor: '#2e7d7b',
              color: 'white',
              py: 2.2,
              borderRadius: '28px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              height: '56px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#1e5a58',
                boxShadow: 'none',
              },
              ...(loading ? { backgroundColor: '#999', cursor: 'not-allowed' } : {}),
            }}
          >
            Unlock
          </Button>
        )}
      </Container>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pb: 2,
        }}
      >
        <Box
          sx={{
            width: 134,
            height: 5,
            backgroundColor: '#000',
            borderRadius: 2.5,
          }}
        />
      </Box>
    </Box>
  );
}
